import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { managedDevicesApi } from '../services/managedDevicesApi'
import { getTenantId } from '../config/tenant'
import { useSignalR } from './useSignalR'
import { useCallback } from 'react'

// Main hook for fetching managed devices (routers + hosts)
export const useManagedDevices = (kind = null) => {
  const tenantId = getTenantId()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['managedDevices', tenantId, kind],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('tenantId not found')
      }
      return managedDevicesApi.getByTenant(tenantId, kind)
    },
    enabled: !!tenantId,
    // Async polling: more frequent when tab is visible; pauses in background
    refetchInterval: () =>
      typeof document !== 'undefined' && document.hidden ? false : 8000,
    refetchIntervalInBackground: false,
    // Don't stop updating when window is in focus
    refetchOnWindowFocus: true,
    // Always show updated data, even during refetch
    keepPreviousData: false,
  })

  // Callback to update data when receiving SignalR notification
  const handleStatusChange = useCallback((data) => {
    // Update React Query cache with new data
    queryClient.setQueryData(['managedDevices', tenantId, kind], (oldData) => {
      if (!oldData) {
        queryClient.invalidateQueries({ queryKey: ['managedDevices', tenantId, kind] })
        return oldData
      }

      const updated = oldData.map((device) => {
        // Compare IDs as strings to avoid type issues
        const deviceIdStr = String(device.id)
        const dataDeviceIdStr = String(data.deviceId || data.DeviceId || data.routerId || data.RouterId || data.hostId || data.HostId)

        if (deviceIdStr === dataDeviceIdStr) {
          const newStatus = data.status || data.Status
          const newLastSeenAt = data.lastSeenAt || data.LastSeenAt
          const newLatency = data.latency !== undefined ? data.latency : (data.Latency !== undefined ? data.Latency : device.latency)

          // Always create new object to ensure React detects the change
          const updatedDevice = {
            ...device,
            status: newStatus,
            lastSeenAt: newLastSeenAt,
            latency: newLatency,
          }

          // Router-specific status updates
          if (managedDevicesApi.isRouter(device)) {
            updatedDevice.apiAuthStatus =
              data.apiAuthStatus !== undefined
                ? data.apiAuthStatus
                : data.ApiAuthStatus !== undefined
                  ? data.ApiAuthStatus
                  : device.apiAuthStatus

            updatedDevice.apiAuthCheckedAt =
              data.apiAuthCheckedAt ?? data.ApiAuthCheckedAt ?? device.apiAuthCheckedAt

            updatedDevice.apiAuthMessage =
              data.apiAuthMessage !== undefined
                ? data.apiAuthMessage
                : data.ApiAuthMessage !== undefined
                  ? data.ApiAuthMessage
                  : device.apiAuthMessage
          }

          // Host-specific status updates
          if (managedDevicesApi.isHost(device)) {
            updatedDevice.sshInteractiveSessionOpen =
              data.sshInteractiveSessionOpen ?? data.SshInteractiveSessionOpen ?? device.sshInteractiveSessionOpen

            updatedDevice.vpnPeerLastHandshake =
              data.vpnPeerLastHandshake ?? data.VpnPeerLastHandshake ?? device.vpnPeerLastHandshake

            updatedDevice.vpnPeerPingSuccess =
              data.vpnPeerPingSuccess !== undefined
                ? data.vpnPeerPingSuccess
                : data.VpnPeerPingSuccess !== undefined
                  ? data.VpnPeerPingSuccess
                  : device.vpnPeerPingSuccess
          }

          return updatedDevice
        }
        return device
      })

      return updated
    })
  }, [queryClient, tenantId, kind])

  // Subscribe to SignalR status updates
  useSignalR('DeviceStatusChanged', handleStatusChange)
  // Maintain compatibility with legacy SignalR events
  useSignalR('RouterStatusChanged', handleStatusChange)
  useSignalR('HostStatusChanged', handleStatusChange)

  return query
}

// Convenience hooks for specific device types
export const useRouters = () => {
  return useManagedDevices('Router')
}

export const useHosts = () => {
  return useManagedDevices('Host')
}

// Hook for single managed device
export const useManagedDevice = (id) => {
  return useQuery({
    queryKey: ['managedDevice', id],
    queryFn: () => managedDevicesApi.getById(id),
    enabled: !!id,
    staleTime: 30000, // Cache for 30 seconds
  })
}

// Backward compatibility
export const useRouter = (id) => useManagedDevice(id)
export const useHost = (id) => useManagedDevice(id)

// Mutation hooks for managed devices
export const useCreateManagedDevice = () => {
  const tenantId = getTenantId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data) => managedDevicesApi.create(tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedDevices', tenantId] })
    },
  })
}

export const useUpdateManagedDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => managedDevicesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managedDevice', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['managedDevices'] })
    },
  })
}

export const useDeleteManagedDevice = () => {
  const tenantId = getTenantId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id) => managedDevicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedDevices', tenantId] })
    },
  })
}

export const useTestManagedDeviceConnection = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id) => managedDevicesApi.testConnection(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['managedDevice', id] })
      queryClient.invalidateQueries({ queryKey: ['managedDevices'] })
    },
  })
}

// Router-specific mutations
export const useActivateBootstrap = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id) => managedDevicesApi.activateBootstrap(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['managedDevice', id] })
      queryClient.invalidateQueries({ queryKey: ['managedDevices'] })
    },
  })
}

// Host-specific mutations
export const useActivateSetup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id) => managedDevicesApi.activateSetup(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['managedDevice', id] })
      queryClient.invalidateQueries({ queryKey: ['managedDevices'] })
    },
  })
}

// Group management mutations
export const useMoveManagedDeviceToGroup = () => {
  const tenantId = getTenantId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, groupId }) => managedDevicesApi.moveToGroup(id, groupId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managedDevice', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['managedDevices'] })
      queryClient.invalidateQueries({ queryKey: ['managedDeviceGroups', tenantId] })
    },
  })
}

export const useUpdateManagedDeviceIcon = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, customIconBase64 }) => managedDevicesApi.updateIcon(id, customIconBase64),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['managedDevice', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['managedDevices'] })
    },
  })
}

// Backward compatibility exports
export const useCreateRouter = useCreateManagedDevice
export const useUpdateRouter = useUpdateManagedDevice
export const useDeleteRouter = useDeleteManagedDevice
export const useTestRouterConnection = useTestManagedDeviceConnection

export const useCreateHost = useCreateManagedDevice
export const useUpdateHost = useUpdateManagedDevice
export const useDeleteHost = useDeleteManagedDevice

export const useMoveRouterToGroup = useMoveManagedDeviceToGroup
export const useMoveHostToGroup = useMoveManagedDeviceToGroup
export const useUpdateRouterIcon = useUpdateManagedDeviceIcon
export const useUpdateHostIcon = useUpdateManagedDeviceIcon